import { useEffect } from "react";

import {
  ahPostureLabel,
  RL_PRIORITY_LABEL,
  type EnrichedMapAccount,
} from "./portfolioMapVisualHelpers";
import { useSignalPresentation } from "./useSignalPresentation";
import { useHealthScoreThresholds } from "../../Hooks/useHealthScoreThresholds";
import type { PortfolioAccountRow } from "../../Store/Reducers/MyPortfolioOverviewSlice";

interface Props {
  open: boolean;
  accounts: EnrichedMapAccount[];
  /** For the AL join — PortfolioMapAccount has no `al` field (it lives on
   * the separate my-portfolio/overview endpoint). */
  overviewAccounts: PortfolioAccountRow[];
  /** Working-set label for the subtitle (mock pmWorkingSetLabel). */
  subtitleLabel: string;
  /** Quadrant sets keep their boundary toggle in the modal too (mock
   * renderPMComparisonModal). */
  boundaryControl?: { checked: boolean; onToggle: () => void };
  onClose: () => void;
  /** Omitted when Signal service / Open Actions is off — button is not shown. */
  onReviewActions?: () => void;
  onReviewPriorities: () => void;
  onReviewGoals: () => void;
}

function formatNet(net: number | null): string {
  if (net == null) return "—";
  const rounded = Math.round(net);
  return rounded >= 0 ? `+${rounded}` : `${rounded}`;
}

const BH_CLASS_LABEL: Record<string, string> = {
  strong: "strong",
  stable: "stable",
  concern: "concern",
  no_data: "no data",
};

/** Mock's `.pm-comparison-modal` table (my-accounts-portfolio-v4.html
 * ~6664-6696, ~8830-8845) opened from the "Compare N accounts" button in
 * the lens bar — a read-only table snapshot of the current manual
 * selection, distinct from (and, per the mock's actual click-handler logic,
 * the ONLY reachable comparison surface for a shift-click-built selection —
 * the mock's inline right-panel card list is only reachable from a
 * quadrant/matrix-cell/availability-gutter region click, which this app
 * replaced with drag-marquee selection sharing the same manual selection
 * state). Closing this modal does not clear the selection. */
const PortfolioMapComparisonModal: React.FC<Props> = ({
  open,
  accounts,
  overviewAccounts,
  subtitleLabel,
  boundaryControl,
  onClose,
  onReviewActions,
  onReviewPriorities,
  onReviewGoals,
}) => {
  const { signalsVisible } = useSignalPresentation();
  const { accountHealth: ahThresholds } = useHealthScoreThresholds();
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Claim the key so PortfolioMapTab's own Escape handler (on window,
      // which runs after this document listener) leaves the selection be.
      e.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const alByAccountId = new Map(
    overviewAccounts.map(a => [a.customer_id, a.al_level])
  );

  return (
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/45 p-5"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex max-h-[90vh] w-full max-w-[960px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pm-comparison-title"
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-[22px] py-4 pb-[15px]">
          <div>
            <div
              id="pm-comparison-title"
              className="text-[17px] font-extrabold text-gray-900"
            >
              Compare selected accounts
            </div>
            <div className="mt-1 text-xs text-gray-500">
              {accounts.length} account{accounts.length === 1 ? "" : "s"} ·{" "}
              {subtitleLabel}
            </div>
          </div>
          <button
            type="button"
            aria-label="Dismiss comparison"
            onClick={onClose}
            className="p-0.5 text-lg leading-none text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <div className="overflow-x-auto px-[22px] py-4">
          {boundaryControl && (
            <label className="mb-3 flex items-center gap-1.5 rounded-md bg-gray-50 px-2 py-1.5 text-[11px] font-semibold text-gray-600">
              <input
                type="checkbox"
                checked={boundaryControl.checked}
                onChange={boundaryControl.onToggle}
              />
              Include boundary accounts
            </label>
          )}
          <table
            className="w-full border-separate border-spacing-0 text-[11.5px] text-gray-500"
            aria-label="Selected account comparison"
          >
            <thead>
              <tr>
                {[
                  "Account",
                  "Fit",
                  "BH",
                  "AH",
                  "Posture",
                  "AL",
                  "Net",
                  "RL priority",
                  ...(signalsVisible ? ["Signals"] : []),
                ].map((h, i, arr) => (
                  <th
                    key={h}
                    className={`whitespace-nowrap border-y border-gray-200 bg-gray-50 px-[9px] py-[7px] text-left text-[10px] font-semibold uppercase tracking-wide text-gray-400 ${
                      i === 0
                        ? "rounded-tl-md border-l"
                        : i === arr.length - 1
                          ? "rounded-tr-md border-r"
                          : ""
                    }`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {accounts.map(a => {
                const al = alByAccountId.get(a.account_id) ?? null;
                return (
                  <tr key={a.account_id} data-comparison-account={a.account_id}>
                    <td className="whitespace-nowrap border-b border-l border-gray-200 p-[9px] font-extrabold text-gray-900">
                      {a.account_name}
                    </td>
                    <td className="whitespace-nowrap border-b border-gray-200 p-[9px]">
                      {a.fit != null ? Math.round(a.fit) : "—"}
                    </td>
                    <td className="whitespace-nowrap border-b border-gray-200 p-[9px]">
                      {a.bh != null ? Math.round(a.bh) : "—"} ·{" "}
                      {BH_CLASS_LABEL[a.bh_class] ?? a.bh_class}
                    </td>
                    <td className="whitespace-nowrap border-b border-gray-200 p-[9px]">
                      {a.ah != null ? Math.round(a.ah) : "Unscored"}
                    </td>
                    <td className="whitespace-nowrap border-b border-gray-200 p-[9px]">
                      {ahPostureLabel(a.ah, ahThresholds, a.ah_cluster)}
                    </td>
                    <td className="whitespace-nowrap border-b border-gray-200 p-[9px]">
                      {al == null ? (
                        <span title="Unassessed">AL ?</span>
                      ) : (
                        `AL ${al}`
                      )}
                    </td>
                    <td className="whitespace-nowrap border-b border-gray-200 p-[9px]">
                      {formatNet(a.net_investment_signal)}
                    </td>
                    <td
                      className={`whitespace-nowrap border-b border-gray-200 p-[9px] ${
                        signalsVisible ? "" : "border-r"
                      }`}
                    >
                      {a.rl_priority ? RL_PRIORITY_LABEL[a.rl_priority] : "—"}
                    </td>
                    {signalsVisible ? (
                      <td className="whitespace-nowrap border-b border-r border-gray-200 p-[9px]">
                        {a.active_signal_count}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-[22px] py-3.5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-100"
          >
            Dismiss
          </button>
          <button
            type="button"
            onClick={onReviewGoals}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-100"
          >
            Review goals
          </button>
          <button
            type="button"
            onClick={onReviewPriorities}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-100"
          >
            Review priorities
          </button>
          {onReviewActions ? (
            <button
              type="button"
              onClick={onReviewActions}
              className="rounded-md bg-purple-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-purple-800"
            >
              Review actions
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PortfolioMapComparisonModal;
